import React, { useState, useEffect } from "react";
import { X, CheckSquare, Square, Save, Edit2, ChevronDown, ChevronUp } from "lucide-react";
import { db } from "../firebase"; 
import { doc, getDoc, writeBatch } from "firebase/firestore";

// Techniniai raktai duomenų bazei
const DB_DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function ProjectViewModal({
  show,
  onClose,
  project,
  targetKw,
  setTargetKw,
  copyWorkers,
  setCopyWorkers,
  handleCopyToWeek,
  t
}) {
  const [isModalEditMode, setIsModalEditMode] = useState(false);
  const [realTimeData, setRealTimeData] = useState({}); 
  const [loadingData, setLoadingData] = useState(false);
  
  const [dayEdits, setDayEdits] = useState({});
  const [bulkEdits, setBulkEdits] = useState({});
  const [expandedFields, setExpandedFields] = useState({});

  // --- 1. DUOMENŲ UŽKROVIMAS ---
  useEffect(() => {
    if (!show || !project) return;

    const loadRealTimeData = async () => {
      setLoadingData(true);
      const detailsMap = {};
      
      const uniqueWorkerIds = new Set();
      Object.values(project.days || {}).forEach(arr => {
        arr.forEach(w => uniqueWorkerIds.add(typeof w === 'string' ? w : w.id));
      });

      const year = project.year;
      const simpleDate = new Date(year, 0, 1 + (project.kw - 1) * 7);
      const month = (simpleDate.getMonth() + 1).toString().padStart(2, '0');

      await Promise.all(Array.from(uniqueWorkerIds).map(async (wid) => {
        const docId = `${wid}_${year}_${month}`;
        try {
          const snap = await getDoc(doc(db, "monthlyWorkHours", docId));
          if (snap.exists()) {
            detailsMap[wid] = snap.data().days || {};
          }
        } catch (e) {
          console.error("Error loading details:", e);
        }
      }));

      setRealTimeData(detailsMap);
      setLoadingData(false);
    };

    loadRealTimeData();
    setDayEdits({});
    setBulkEdits({});
    setExpandedFields({});
    setIsModalEditMode(false);

  }, [show, project]);

  if (!show || !project) return null;

  // --- PAGALBINĖS FUNKCIJOS ---
  const getDayNum = (dayIndex) => {
      const firstDayDate = new Date(project.year, 0, 1 + (project.kw - 1) * 7);
      const dayOfWeek = firstDayDate.getDay() || 7; 
      firstDayDate.setDate(firstDayDate.getDate() + 4 - dayOfWeek - 3); // Pirmadienis
      const currentDayDate = new Date(firstDayDate);
      currentDayDate.setDate(firstDayDate.getDate() + dayIndex);
      return currentDayDate.getDate().toString();
  };

  // --- PATAISYTA: SKAITO PAGAL PROJECT ID ---
  const getEffectiveValue = (workerId, dayNum, fieldKey) => {
      let dbKey = fieldKey === 'accommodation' ? 'hotel' : fieldKey;

      if (dayEdits[workerId]?.[dayNum]?.[dbKey] !== undefined) {
          return dayEdits[workerId][dayNum][dbKey];
      }
      if (bulkEdits[workerId]?.[fieldKey] !== undefined) {
          return bulkEdits[workerId][fieldKey];
      }
      // Čia esminis pakeitimas: pridedame [project.id]
      const dbVal = realTimeData[workerId]?.[dayNum]?.[project.id]?.[dbKey];
      if (dbVal !== undefined) return dbVal;

      return project.workerDetails?.[workerId]?.[fieldKey] || "";
  };

  // --- PATAISYTA: GYVA PERŽIŪRA PAGAL PROJECT ID ---
  const getLivePreviewString = (workerId, fieldKey) => {
    const grouped = {}; 
    let hasData = false;

    DB_DAYS.forEach((dKey, idx) => {
      const dayList = project.days?.[dKey] || [];
      const isActive = dayList.some(w => (typeof w === 'string' ? w === workerId : w.id === workerId));
      
      if (isActive) {
        hasData = true;
        const dayNum = getDayNum(idx);
        let dbKey = fieldKey === 'accommodation' ? 'hotel' : fieldKey;

        let val = "";
        if (dayEdits[workerId]?.[dayNum]?.[dbKey] !== undefined) {
            val = dayEdits[workerId][dayNum][dbKey];
        } else if (bulkEdits[workerId]?.[fieldKey] !== undefined) {
            val = bulkEdits[workerId][fieldKey];
        } else {
            // Čia esminis pakeitimas: pridedame [project.id]
            val = realTimeData[workerId]?.[dayNum]?.[project.id]?.[dbKey];
            if (!val) val = project.workerDetails?.[workerId]?.[fieldKey] || "";
        }
        
        if (val) {
          if (!grouped[val]) grouped[val] = [];
          grouped[val].push(dKey);
        }
      }
    });

    if (!hasData) return "-";
    const entries = Object.entries(grouped);
    if (entries.length === 0) return ""; 

    return entries.map(([val, days]) => {
      if (days.length === 7) return val;
      const translatedDays = days.map(d => t(`days.${d}`));
      return `${val} (${translatedDays.join(", ")})`;
    }).join("; ");
  };

  // --- PATAISYTA: PERŽIŪRA PAGAL PROJECT ID ---
  const getDisplayValue = (workerId, fieldKey) => {
    const workerDays = realTimeData[workerId];
    if (!workerDays) return project.workerDetails?.[workerId]?.[fieldKey] || "-";

    const grouped = {}; 
    DB_DAYS.forEach((dKey, idx) => {
      const dayList = project.days?.[dKey] || [];
      const isActive = dayList.some(w => (typeof w === 'string' ? w === workerId : w.id === workerId));
      
      if (isActive) {
        const dayNum = getDayNum(idx);
        let dbKey = fieldKey === 'accommodation' ? 'hotel' : fieldKey;
        // Čia esminis pakeitimas: pridedame [project.id]
        const val = workerDays[dayNum]?.[project.id]?.[dbKey];
        
        if (val) {
          if (!grouped[val]) grouped[val] = [];
          grouped[val].push(dKey);
        }
      }
    });

    const entries = Object.entries(grouped);
    if (entries.length === 0) return project.workerDetails?.[workerId]?.[fieldKey] || "-";

    return entries.map(([val, days]) => {
      if (days.length === 7) return val;
      const translatedDays = days.map(d => t(`days.${d}`));
      return `${val} (${translatedDays.join(", ")})`;
    }).join("; ");
  };

  const toggleFieldExpand = (workerId, fieldKey) => {
      const key = `${workerId}_${fieldKey}`;
      setExpandedFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleBulkChange = (workerId, field, value) => {
      setBulkEdits(prev => ({
          ...prev,
          [workerId]: { ...(prev[workerId] || {}), [field]: value }
      }));
  };

  const handleDayChange = (workerId, dayNum, field, value) => {
      setDayEdits(prev => ({
          ...prev,
          [workerId]: {
              ...(prev[workerId] || {}),
              [dayNum]: { ...(prev[workerId]?.[dayNum] || {}), [field]: value }
          }
      }));
  };

  // --- PATAISYTA: SKAITO ĮVESTĮ PAGAL PROJECT ID ---
  const getDayInputValue = (workerId, dayNum, field) => {
      const dbKey = field === 'accommodation' ? 'hotel' : field;
      if (dayEdits[workerId]?.[dayNum]?.[dbKey] !== undefined) return dayEdits[workerId][dayNum][dbKey];
      if (bulkEdits[workerId]?.[field] !== undefined) return bulkEdits[workerId][field];
      // Čia esminis pakeitimas: pridedame [project.id]
      const dbVal = realTimeData[workerId]?.[dayNum]?.[project.id]?.[dbKey];
      if (dbVal !== undefined) return dbVal;
      return project.workerDetails?.[workerId]?.[field] || "";
  };

  // --- PATAISYTA: SAUGOJIMO LOGIKA (SVARBIAUSIA) ---
  const handleSmartSave = async () => {
    const batch = writeBatch(db);
    let hasChanges = false;
    
    const affectedWorkers = new Set([...Object.keys(bulkEdits), ...Object.keys(dayEdits)]);

    for (const workerId of affectedWorkers) {
        // Backup (bendri nustatymai projekte - lieka kaip buvę)
        const updates = {};
        ['accommodation', 'transport', 'phone', 'contact', 'comment'].forEach(key => {
            if (bulkEdits[workerId]?.[key] !== undefined) {
                updates[`workerDetails.${workerId}.${key}`] = bulkEdits[workerId][key];
            }
        });
        if (Object.keys(updates).length > 0) {
            const projRef = doc(db, "projects", project.id);
            batch.update(projRef, updates);
            hasChanges = true;
        }

        // Days (SVARBU: čia pridedame project.id į kelią)
        const activeIndices = [];
        DB_DAYS.forEach((dKey, idx) => {
             if (project.days?.[dKey]?.some(w => (typeof w === 'string' ? w === workerId : w.id === workerId))) {
                 activeIndices.push(idx);
             }
        });

        const year = project.year;
        const simpleDate = new Date(year, 0, 1 + (project.kw - 1) * 7);
        const month = (simpleDate.getMonth() + 1).toString().padStart(2, '0');
        const docId = `${workerId}_${year}_${month}`;
        const empRef = doc(db, "monthlyWorkHours", docId);

        activeIndices.forEach(idx => {
            const dayNum = getDayNum(idx);
            const dayUpdate = {};
            
            const newHotel = getLiveValueForSave(workerId, dayNum, 'accommodation');
            const newTransport = getLiveValueForSave(workerId, dayNum, 'transport');
            const newPhone = getLiveValueForSave(workerId, dayNum, 'phone');
            const newContact = getLiveValueForSave(workerId, dayNum, 'contact');
            const newComment = getLiveValueForSave(workerId, dayNum, 'comment');

            // --- ČIA KEIČIASI KELIAS Į DUOMENŲ BAZĘ ---
            // Vietoj `days.12.comment` dabar bus `days.12.PROJECT_ID.comment`
            if (newHotel !== undefined) dayUpdate[`days.${dayNum}.${project.id}.hotel`] = newHotel;
            if (newTransport !== undefined) dayUpdate[`days.${dayNum}.${project.id}.transport`] = newTransport;
            if (newPhone !== undefined) dayUpdate[`days.${dayNum}.${project.id}.phone`] = newPhone;
            if (newContact !== undefined) dayUpdate[`days.${dayNum}.${project.id}.contact`] = newContact;
            if (newComment !== undefined) dayUpdate[`days.${dayNum}.${project.id}.comment`] = newComment;
            
            if (Object.keys(dayUpdate).length > 0) {
                dayUpdate[`days.${dayNum}.${project.id}.updatedAt`] = Date.now();
                batch.update(empRef, dayUpdate);
            }
        });
    }

    try {
      await batch.commit();
      onClose(); 
    } catch (e) {
      console.error("Save error:", e);
      alert(t('error'));
    }
  };

  const getLiveValueForSave = (workerId, dayNum, fieldKey) => {
      const dbKey = fieldKey === 'accommodation' ? 'hotel' : fieldKey;
      if (dayEdits[workerId]?.[dayNum]?.[dbKey] !== undefined) return dayEdits[workerId][dayNum][dbKey];
      if (bulkEdits[workerId]?.[fieldKey] !== undefined) return bulkEdits[workerId][fieldKey];
      return undefined;
  };

  const getSummary = (mainValue, detailKey) => {
    const values = new Set();
    if (mainValue) values.add(mainValue);
    if (project.workerDetails) {
      Object.values(project.workerDetails).forEach(det => {
        if (det[detailKey]) values.add(det[detailKey]);
      });
    }
    return Array.from(values).join(", ");
  };

  const accommodationSummary = getSummary(project.info.unterkunft, 'accommodation');
  const transportSummary = getSummary(project.info.transport, 'transport');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <style>{`
        .invisible-scroll { scrollbar-width: none !important; }
        .invisible-scroll::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
        
        .day-edit-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 8px;
            background: #2a2a2a;
            padding: 10px;
            border-radius: 6px;
            margin-top: 5px;
            border: 1px solid #444;
            max-height: 200px; 
            overflow-y: auto;
            scrollbar-width: none;
        }
        .day-edit-grid::-webkit-scrollbar { display: none; }

        .day-edit-item {
            background: #111;
            padding: 5px;
            border-radius: 4px;
            border: 1px solid #333;
        }
        .day-label {
            font-size: 10px;
            color: #00ff88;
            margin-bottom: 2px;
            text-transform: uppercase;
        }
        .day-mini-input {
            width: 100%;
            background: transparent;
            border: none;
            color: #fff;
            font-size: 11px;
            outline: none;
            border-bottom: 1px solid #555;
        }
        .day-mini-input:focus { border-bottom-color: #00ff88; }

        .input-wrapper { position: relative; display: flex; align-items: center; }
        
        .expand-arrow {
            position: absolute;
            right: 5px;
            color: #00ff88;
            cursor: pointer;
            padding: 2px;
            border-radius: 4px;
            z-index: 10;
        }
        .expand-arrow:hover { background: rgba(0,255,136, 0.1); }
      `}</style>
      
      <div className="project-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{project.info.bauvorhaben}</h2>
          <button className="close-x" onClick={onClose}><X size={24} /></button>
        </div>

        <div className="modal-copy-bar">
          <span style={{ fontSize: '14px', color: '#888', fontWeight: '700' }}>{t('select_kw')}:</span>
          <input type="number" value={targetKw} onChange={(e) => setTargetKw(e.target.value)} className="kw-input-large" placeholder={t('kw')} />
          <div className="copy-checkbox-group" onClick={() => setCopyWorkers(!copyWorkers)}>
            {copyWorkers ? <CheckSquare color="#00ff88" size={24} /> : <Square color="#666" size={24} />}
            <span>{t('with_workers')}</span>
          </div>
          <button className="copy-action-btn" onClick={handleCopyToWeek}>{t('copy_to_kw') || "PERKELTI"}</button>
        </div>

        <div className="modal-body invisible-scroll" style={{ overflowY: 'auto' }}>
          <div className="worker-detailed-card" style={{ marginBottom: 20 }}>
            <div className="read-only-info-grid">
              <div className="info-item"><label>{t('client')}:</label><span>{project.info.auftraggeber || "-"}</span></div>
              <div className="info-item"><label>{t('project')}:</label><span>{project.info.bauvorhaben || "-"}</span></div>
              <div className="info-item full-width"><label>{t('address')}:</label><span>{project.info.adresse || "-"}</span></div>
              <div className="info-item"><label>{t('contact_person')}:</label><span>{project.info.ansprechpartner || "-"}</span></div>
              <div className="info-item"><label>{t('contact')}:</label><span>{project.info.kontakt || "-"}</span></div>
              <div className="info-item full-width"><label>{t('accommodation')}:</label><span style={{color: '#00b7ff'}}>{accommodationSummary || "-"}</span></div>
              <div className="info-item"><label>{t('phone')}:</label><span>{project.info.tel || "-"}</span></div>
              <div className="info-item full-width"><label>{t('transport')}:</label><span style={{color: '#00ff88'}}>{transportSummary || "-"}</span></div>
              <div className="info-item full-width"><label>{t('comment')}:</label><span>{project.info.kommentar || "-"}</span></div>
            </div>
          </div>

          <div className="m-workers-cards">
            {(() => {
              const uniqueWorkers = new Map();
              Object.values(project.days || {}).forEach(arr => {
                arr.forEach(w => {
                  const id = typeof w === 'string' ? w : w.id;
                  const name = typeof w === 'string' ? '...' : w.name;
                  if (!uniqueWorkers.has(id)) uniqueWorkers.set(id, name);
                });
              });

              return Array.from(uniqueWorkers.entries()).map(([id, name]) => {
                const activeIndices = [];
                const activeDays = DB_DAYS.map((dKey, idx) => {
                    const dayList = project.days?.[dKey] || [];
                    const isActive = dayList.some(w => (typeof w === 'string' ? w === id : w.id === id));
                    if(isActive) activeIndices.push(idx);
                    return { key: dKey, active: isActive };
                });

                return (
                  <div key={id} className="worker-detailed-card">
                    <div className="worker-card-header">
                      <span className="worker-name-big">{name}</span>
                      <div className="worker-days-row">
                        {activeDays.map(d => (
                          <span key={d.key} className={`day-badge ${d.active ? 'active' : 'inactive'}`}>{t(`days.${d.key}`)}</span>
                        ))}
                      </div>
                    </div>
                    
                    <div className={isModalEditMode ? "editable-info-grid" : "read-only-info-grid"}>
                      {[
                        { label: t('accommodation'), key: "accommodation" },
                        { label: t('transport'), key: "transport" },
                        { label: t('phone'), key: "phone" },
                        { label: t('contact'), key: "contact" },
                        { label: t('comment'), key: "comment", full: true }
                      ].map((field) => {
                          const livePreview = getLivePreviewString(id, field.key);

                          if (!isModalEditMode) {
                            const displayValue = loadingData ? "..." : getDisplayValue(id, field.key);
                            return (
                               <div key={field.key} className={`edit-group ${field.full ? "full-width" : ""}`}>
                                 <label>{field.label}</label>
                                 <span style={{whiteSpace: 'pre-wrap', lineHeight: '1.2'}}>{displayValue}</span>
                               </div>
                            );
                          }

                          const isFieldExpanded = expandedFields[`${id}_${field.key}`];
                          const bulkValue = bulkEdits[id]?.[field.key] || "";

                          return (
                            <div key={field.key} className={`edit-group ${field.full ? "full-width" : ""}`}>
                              <label>{field.label}</label>
                              
                              <div className="input-group-vertical">
                                   <div className="input-wrapper">
                                     <input
                                       value={bulkValue}
                                       onChange={(e) => handleBulkChange(id, field.key, e.target.value)}
                                       placeholder={livePreview || "..."}
                                       style={{paddingRight: '25px'}} 
                                     />
                                     <div 
                                        className="expand-arrow"
                                        onClick={() => toggleFieldExpand(id, field.key)}
                                     >
                                          {isFieldExpanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                                     </div>
                                   </div>

                                   {isFieldExpanded && (
                                     <div className="day-edit-grid">
                                          {activeIndices.map(idx => {
                                               const dKey = DB_DAYS[idx];
                                               const dayNum = getDayNum(idx);
                                               let dbKey = field.key === 'accommodation' ? 'hotel' : field.key;
                                               
                                               const currentVal = getDayInputValue(id, dayNum, field.key);

                                               return (
                                                   <div key={idx} className="day-edit-item">
                                                        <div className="day-label">{t(`days.${dKey}`)}</div>
                                                        <input 
                                                            className="day-mini-input"
                                                            value={currentVal}
                                                            onChange={(e) => handleDayChange(id, dayNum, dbKey, e.target.value)}
                                                        />
                                                   </div>
                                               );
                                          })}
                                     </div>
                                   )}
                               </div>
                           </div>
                          );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        <div className="modal-footer">
          <div className="modal-footer-row">
            {isModalEditMode ? (
              <>
                <button className="m-btn cancel" onClick={() => { setIsModalEditMode(false); setDayEdits({}); setBulkEdits({}); setExpandedFields({}); }}>{t('cancel')}</button>
                <button className="m-btn save" onClick={handleSmartSave}><Save size={18} style={{marginRight:5}}/> {t('save')}</button>
              </>
            ) : (
              <>
                <button className="m-btn eye-close-gray" onClick={onClose}>{t('close')}</button>
                <button className="m-btn eye-edit-blue" onClick={() => setIsModalEditMode(true)}><Edit2 size={18} style={{marginRight:5}}/> {t('edit')}</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}